import { IANAZone, Zone, ZoneOffsetFormat, ZoneOffsetOptions } from "luxon";
import moment_tz from "moment-timezone";

/**
 * CN-2932: luxon resolves a named zone through Intl, which reads the browser's tz data.
 * Safari reads the OS data, and macOS and iOS can lag behind a rule change: without
 * tzdata 2026b, Alberta, BC and Manitoba still "fall back" on 1 Nov 2026, so every
 * session and program in those zones shows one hour early, and a time picked in the
 * form is saved one hour late. This zone reads the tzdata bundled with
 * moment-timezone instead, so every browser shows the times the backend stores.
 *
 * Luxon formats a zone whose type is not "iana" by shifting to UTC, so Intl never
 * sees the zone name and the browser data is not used for formatting either.
 */
class TzdataZone extends Zone {
  private readonly tz: moment_tz.MomentZone;
  private readonly browserZone: Zone;

  constructor(tz: moment_tz.MomentZone, browserZone: Zone) {
    super();
    this.tz = tz;
    this.browserZone = browserZone;
  }

  get type() {
    return "tzdata";
  }

  get name() {
    return this.tz.name;
  }

  get isUniversal() {
    return false;
  }

  get isValid() {
    return true as const;
  }

  offsetName(ts: number, options: ZoneOffsetOptions) {
    // Keep the browser's names ("MDT", "GMT+2", "Mountain Daylight Time") while its
    // data agrees with tzdata. When it does not, its names describe the wrong offset;
    // tzdata has no long names, so both formats get the tzdata abbreviation.
    if (this.browserZone.isValid && this.browserZone.offset(ts) === this.offset(ts)) {
      return this.browserZone.offsetName(ts, options);
    }
    return this.tz.abbr(ts);
  }

  formatOffset(ts: number, format: ZoneOffsetFormat) {
    const offset = this.offset(ts);
    const sign = offset >= 0 ? "+" : "-";
    const hours = Math.trunc(Math.abs(offset) / 60);
    const minutes = Math.abs(offset) % 60;
    const hh = String(hours).padStart(2, "0");
    const mm = String(minutes).padStart(2, "0");

    if (format === "narrow") {
      return `${sign}${hours}${minutes ? `:${minutes}` : ""}`;
    }
    if (format === "techie") {
      return `${sign}${hh}${mm}`;
    }
    return `${sign}${hh}:${mm}`;
  }

  offset(ts: number) {
    return -this.tz.utcOffset(ts);
  }

  equals(other: Zone) {
    return other.type === this.type && other.name === this.name;
  }
}

const createBrowserZone = IANAZone.create.bind(IANAZone);
const zones = new Map<string, Zone>();

// Every named zone luxon resolves (setZone("America/Edmonton"), { zone: ... },
// Settings.defaultZone) goes through IANAZone.create.
IANAZone.create = ((name: string) => {
  const tz = moment_tz.tz.zone(name);
  if (!tz) {
    return createBrowserZone(name);
  }

  let zone = zones.get(name);
  if (!zone) {
    zone = new TzdataZone(tz, createBrowserZone(name));
    zones.set(name, zone);
  }
  return zone;
}) as typeof IANAZone.create;

// The system zone (DateTime.local(), the default zone) stays on the browser data on
// purpose: date pickers and other native Date code use that data too, so the two
// agree on which calendar day an instant falls on.
